import { useMemo } from "react";

import { useChangesHistoryContext } from "../context";
import { useThemesContext } from "../../../context/ThemeProvider";

export const getIframeWindow = () => {
  const iframe = document.getElementById('site-iframe');
  return iframe?.contentWindow;
};

// Enhanced function to safely send postMessage with retry
const safePostMessage = (message, retries = 3) => {
  const attemptSend = (attempt) => {
    const iframeWindow = getIframeWindow();
    
    if (iframeWindow) {
      try {
        iframeWindow.postMessage(message, '*');
        console.log('PostMessage sent successfully:', message.eventName);
        return true;
      } catch (error) {
        console.error('PostMessage failed:', error);
      }
    }
    
    if (attempt < retries) {
      console.warn(`PostMessage attempt ${attempt} failed, retrying...`);
      setTimeout(() => attemptSend(attempt + 1), 100);
    } else {
      console.error('PostMessage failed after all attempts');
    }
    return false;
  };
  
  attemptSend(1);
};

const useIFrameEvents = (templateKey, sectionKey, blockKey) => {
  const {
    selectedTheme,
    selectedTemplate,
    pages,
    products,
    feedbacks,
    tickets,
    invoices,
  } = useThemesContext();

  const {
    currentState,
    themeCache,
    initThemeTemplateCache,
    handleIframeNavigate,
  } = useChangesHistoryContext();

  const layoutName = useMemo(() => {
    if (selectedTemplate?.layout_name) {
      return selectedTemplate?.layout_name;
    }

    if (currentState?.layouts) {
      return Object.values(currentState?.layouts)?.find(
        ({ is_default }) => !!is_default,
      )?.name;
    }
  }, [currentState?.layouts, selectedTemplate]);

  const eventData = useMemo(
    () => ({
      themeId: selectedTheme?.id,
      layoutId: layoutName ?? undefined,
      templateId:
        templateKey &&
        selectedTemplate &&
        selectedTemplate?.name === templateKey
          ? selectedTemplate?.name
          : undefined,
      sectionName: sectionKey ?? undefined,
    }),
    [selectedTheme, layoutName, selectedTemplate, templateKey, sectionKey],
  );

  const renderTemplateEvent = settings => {
    const message = {
      eventName: 'PaylixBuilderRenderTemplateEvent',
      data: {
        ...eventData,
        templateId: selectedTemplate?.name,
        settings,
      },
    };
    safePostMessage(message);
  };

  const renderSectionEvent = settings => {
    const message = {
      eventName: 'PaylixBuilderRenderSectionEvent',
      data: {
        ...eventData,
        templateId: selectedTemplate?.name,
        settings,
      },
    };
    safePostMessage(message);
  };

  const renderBlockEvent = settings => {
    const message = {
      eventName: 'PaylixBuilderRenderBlockEvent',
      data: {
        ...eventData,
        templateId: selectedTemplate?.name,
        blockName: blockKey,
        settings,
      },
    };
    safePostMessage(message);
  };

  const scrollToSectionEvent = () => {
    const message = {
      eventName: 'PaylixBuilderScrollToSectionEvent',
      data: {
        ...eventData,
      },
    };
    safePostMessage(message);
  };

  const hoverSectionEvent = () => {
    const message = {
      eventName: 'PaylixBuilderHoverSectionEvent',
      data: {
        ...eventData,
      },
    };
    safePostMessage(message);
  };

  const lockSectionEvent = () => {
    const message = {
      eventName: 'PaylixBuilderLockSectionEvent',
      data: {
        ...eventData,
      },
    };
    safePostMessage(message);
  };

  const hoverBlockEvent = () => {
    const message = {
      eventName: 'PaylixBuilderHoverBlockEvent',
      data: {
        ...eventData,
        blockName: blockKey,
      },
    };
    safePostMessage(message);
  };

  const resetHighlight = () => {
    const message = {
      eventName: 'PaylixBuilderResetHighlightEvent',
      data: {
        themeId: selectedTheme?.id,
      },
    };
    safePostMessage(message);
  };

  const resetLock = () => {
    const message = {
      eventName: 'PaylixBuilderResetLockEvent',
      data: {
        themeId: selectedTheme?.id,
      },
    };
    safePostMessage(message);
  };

  //HANDLERS
  const onChangeTemplate = async template => {
    const isProductTemplate = template.template_type === 'PRODUCT';
    const isFeedbackTemplate = template.template_type === 'FEEDBACK';
    const isTicketTemplate = template.template_type === 'TICKET';
    const isInvoiceTemplate = template.template_type === 'INVOICE';
    const isDeliveryTemplate = template.template_type === 'DELIVERY';

    let slug = pages?.find(
      page => page.template_type === template.template_type,
    )?.slug;

    let cachedTemplate;
    if (themeCache?.cache) {
      cachedTemplate = themeCache?.cache.renderOptions.templates[template.name];
    }

    if (isProductTemplate) {
      const productUniqId = products[0]?.uniqid;

      slug = slug.replace(':id', productUniqId);
    }

    if (isFeedbackTemplate) {
      const feedbackUniqId = feedbacks?.[0]?.uniqid || '000000-0000000000-000000';

      slug = slug.replace(':id', feedbackUniqId);
    }

    if (isTicketTemplate) {
      const ticketUniqId = tickets[0]?.uniqid;

      slug = slug.replace(':id', ticketUniqId);
    }

    if (isInvoiceTemplate) {
      const uncompletedInvoices = invoices.filter((invoice) => invoice.status !== 'COMPLETED');
      const invoiceUniqId = (uncompletedInvoices.length ? uncompletedInvoices[0] : invoices[0])?.uniqid;

      slug = slug.replace(':id', invoiceUniqId);
    }

    if (isDeliveryTemplate) {
      const completedInvoices = invoices.filter((invoice) => invoice.status === 'COMPLETED');
      const deliveryUniqId = (completedInvoices.length ? completedInvoices[0] : invoices[0])?.uniqid;

      slug = slug.replace(':id', deliveryUniqId);
    }

    !cachedTemplate && (await initThemeTemplateCache(themeCache, template));
    handleIframeNavigate(slug);
  };

  const onHoverSectionItem = () => {
    scrollToSectionEvent();
    hoverSectionEvent();
  };

  const onHoverBlockItem = () => {
    scrollToSectionEvent();
    hoverBlockEvent();
  };

  const renderTemplate = settings => {
    renderTemplateEvent(settings);
  };

  const renderSection = settings => {
    renderSectionEvent(settings);
  };

  const renderBlock = settings => {
    renderBlockEvent(settings);
  };

  return {
    onChangeTemplate,
    onHoverSectionItem,
    onHoverBlockItem,
    resetHighlight,
    renderTemplateEvent,
    renderTemplate,
    renderSection,
    renderBlock,
    lockSectionEvent,
    resetLock,
  };
};

export default useIFrameEvents;
